import React, { useState, useEffect, useMemo } from 'react';
import { Task } from '../types';
import { Play, Pause, Clock, X, Square } from 'lucide-react';

interface GlobalTimerWidgetProps {
  allTasks: Task[];
  allProjects: any[];
  updateTask: (id: string | number, updates: Partial<Task>) => void;
  onTaskSelect?: (task: Task) => void;
}

const isStatusInProgress = (statusStr?: string) => {
  const s = (statusStr || '').toLowerCase();
  return s === 'pending' || s === 'in_progress' || s === 'in_arbeit' || s === 'arbeit';
};

export const GlobalTimerWidget: React.FC<GlobalTimerWidgetProps> = ({
  allTasks,
  allProjects,
  updateTask,
  onTaskSelect
}) => {
  const [elapsed, setElapsed] = useState(0);
  const [lastActiveTaskId, setLastActiveTaskId] = useState<string | number | null>(null);

  // Find currently running task
  const activeTask = useMemo(() => {
    const task = allTasks.find(t => t.timerStartedAt && t.timerStartedAt > 0);
    if (task) {
      return task;
    }
    return null;
  }, [allTasks]);

  // Update last active task ID when activeTask is running
  useEffect(() => {
    if (activeTask) {
      setLastActiveTaskId(activeTask.id);
    }
  }, [activeTask]);

  // If no active task, check if the last active task still qualifies (is in progress and has tracked time)
  const pausedTask = useMemo(() => {
    if (activeTask) return null;
    if (!lastActiveTaskId) return null;
    const task = allTasks.find(t => t.id === lastActiveTaskId);
    if (task && !task.timerStartedAt && !task.completed) {
      return task;
    }
    return null;
  }, [activeTask, lastActiveTaskId, allTasks]);

  // Live ticking when a task is active
  useEffect(() => {
    if (!activeTask || !activeTask.timerStartedAt) return;

    const calcElapsed = () => {
      const base = activeTask.trackedTime || 0;
      const running = Date.now() - activeTask.timerStartedAt!;
      setElapsed(base + running);
    };

    calcElapsed();
    const interval = setInterval(calcElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeTask]);

  const handlePause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!activeTask || !activeTask.timerStartedAt) return;
    const sessionTime = Date.now() - activeTask.timerStartedAt;
    const newTracked = (activeTask.trackedTime || 0) + sessionTime;
    updateTask(activeTask.id, {
      timerStartedAt: null as any,
      trackedTime: newTracked
    });
  };

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const targetTask = pausedTask;
    if (!targetTask) return;
    updateTask(targetTask.id, {
      timerStartedAt: Date.now()
    });
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    const taskToPause = activeTask;
    if (taskToPause && taskToPause.timerStartedAt) {
      const sessionTime = Date.now() - taskToPause.timerStartedAt;
      const newTracked = (taskToPause.trackedTime || 0) + sessionTime;
      updateTask(taskToPause.id, {
        timerStartedAt: null as any,
        trackedTime: newTracked
      });
    }
    setLastActiveTaskId(null);
  };

  const formatTimeSeconds = (ms: number) => {
    const totalSecs = Math.floor(ms / 1000);
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  };

  const formatTimeMinutes = (ms: number) => {
    const totalSecs = Math.floor(ms / 1000);
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    return `${hrs} Std. ${mins} Min.`;
  };

  const currentProjectName = useMemo(() => {
    const task = activeTask || pausedTask;
    if (!task) return '';
    const proj = allProjects.find(p => p.id === task.projectId);
    return proj ? proj.name : '';
  }, [activeTask, pausedTask, allProjects]);

  if (activeTask) {
    return (
      <div className="flex items-center gap-2.5 px-3 py-1 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors select-none text-[11px] shrink-0 max-w-[280px] sm:max-w-[360px]">
        {/* Pulsing indicator */}
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
        </span>

        {/* Task and Project info */}
        <div 
          onClick={() => onTaskSelect?.(activeTask)}
          className="flex flex-col min-w-0 cursor-pointer group"
          title={`Aufgabe bearbeiten: ${activeTask.name}`}
        >
          <span className="font-bold text-white truncate group-hover:text-indigo-400 transition-colors">
            {activeTask.name}
          </span>
          {currentProjectName && (
            <span className="text-[9px] text-slate-400 font-semibold truncate uppercase tracking-wider">
              {currentProjectName}
            </span>
          )}
        </div>

        {/* Live Timer */}
        <span className="font-mono text-orange-400 font-extrabold tracking-tight shrink-0 ml-1">
          {formatTimeSeconds(elapsed)}
        </span>

        {/* Pause Button */}
        <button 
          onClick={handlePause}
          className="p-1 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 rounded-lg shrink-0 transition-all active:scale-95 border border-orange-500/20"
          title="Timer pausieren"
        >
          <Pause size={12} fill="currentColor" strokeWidth={0} />
        </button>

        {/* Stop Button */}
        <button 
          onClick={handleClose}
          className="p-1 hover:bg-rose-500/20 text-rose-400 hover:text-rose-350 rounded-lg shrink-0 transition-all active:scale-95 border border-rose-500/20 ml-0.5"
          title="Zeitaufzeichnung beenden & speichern"
        >
          <Square size={12} fill="currentColor" strokeWidth={0} />
        </button>
      </div>
    );
  }

  if (pausedTask) {
    const totalTime = pausedTask.trackedTime || 0;
    return (
      <div className="flex items-center gap-2.5 px-3 py-1 bg-slate-900 border border-white/5 rounded-xl opacity-75 hover:opacity-100 hover:bg-white/5 transition-all select-none text-[11px] shrink-0 max-w-[280px] sm:max-w-[360px]">
        {/* Static indicator */}
        <Clock size={12} className="text-slate-400 shrink-0" />

        {/* Task and Project info */}
        <div 
          onClick={() => onTaskSelect?.(pausedTask)}
          className="flex flex-col min-w-0 cursor-pointer group"
          title={`Aufgabe bearbeiten: ${pausedTask.name}`}
        >
          <span className="font-bold text-slate-355 truncate group-hover:text-indigo-400 transition-colors">
            {pausedTask.name}
          </span>
          {currentProjectName && (
            <span className="text-[9px] text-slate-500 font-semibold truncate uppercase tracking-wider">
              {currentProjectName}
            </span>
          )}
        </div>

        {/* Total Time Paused */}
        <span className="text-slate-400 font-semibold tracking-tight shrink-0 ml-1 font-mono">
          {formatTimeMinutes(totalTime)}
        </span>

        {/* Play Button */}
        <button 
          onClick={handlePlay}
          className="p-1 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 rounded-lg shrink-0 transition-all active:scale-95 border border-emerald-500/20"
          title="Timer fortsetzen"
        >
          <Play size={12} fill="currentColor" strokeWidth={0} />
        </button>

        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="p-1 hover:bg-white/10 text-slate-400 hover:text-white rounded-lg shrink-0 transition-all active:scale-95 border border-transparent ml-0.5"
          title="Zeitaufzeichnung ausblenden"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      </div>
    );
  }

  return null;
};
